import axios from 'axios';

// Lazy import to avoid circular dependency
let store = null;
let logoutUser = null;

const getStore = () => {
  if (!store) {
    store = require('../store/store').store;
  }
  return store;
};

const getLogoutUser = () => {
  if (!logoutUser) {
    logoutUser = require('../store/slices/authSlice').logoutUser;
  }
  return logoutUser;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Helper function to decode JWT token and check expiration
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT token (without verification, just to check expiration)
    const base64Url = token.split('.')[1];
    if (!base64Url) return true;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const decoded = JSON.parse(jsonPayload);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    
    // Token is expired if current time is past expiration time
    return currentTime >= expirationTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // If we can't decode, consider it expired
  }
};

// Helper function to clear authentication data
const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('isAuthenticated');
  delete axios.defaults.headers.common['Authorization'];
};

// Helper function to redirect to login
const redirectToLogin = () => {
  clearAuthData();
  // Only redirect if not already on login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Configure default axios instance
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Set Authorization header from persisted token if available
const persistedToken = localStorage.getItem('token');
if (persistedToken) {
  // Check if token is expired before setting it
  if (!isTokenExpired(persistedToken)) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${persistedToken}`;
  } else {
    // Token is expired, clear it
    clearAuthData();
  }
}

// Request interceptor - add token to requests and validate before sending
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpired(token)) {
        // Token is expired, clear auth data and redirect
        redirectToLogin();
        return Promise.reject(new Error('Token expired'));
      }
      
      // Add token to request
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If data is FormData, remove Content-Type so the browser can set
    // multipart/form-data with the correct boundary automatically.
    // Use AxiosHeaders.delete() (case-insensitive) rather than the JS
    // delete operator, which can silently miss keys in Axios 1.x because
    // AxiosHeaders normalises key casing internally.
    if (config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors and token expiration
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Check if this is already a retry attempt to avoid infinite loops
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        // Clear authentication data
        clearAuthData();
        
        // Dispatch logout action to update Redux state
        getStore().dispatch(getLogoutUser()(null));
        
        // Redirect to login page
        redirectToLogin();
      }
      
      return Promise.reject(new Error('Session expired. Please login again.'));
    }
    
    // Handle token expiration errors from backend
    if (error.response?.data?.message?.toLowerCase().includes('expired') ||
        error.response?.data?.message?.toLowerCase().includes('invalid token') ||
        error.response?.data?.message?.toLowerCase().includes('unauthorized')) {
      
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        clearAuthData();
        getStore().dispatch(getLogoutUser()(null));
        redirectToLogin();
      }
      
      return Promise.reject(new Error('Session expired. Please login again.'));
    }
    
    // For other errors, just reject with the original error
    return Promise.reject(error);
  }
);

// Export a function to check token validity (can be used by components)
export const checkTokenValidity = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    return false;
  }
  
  if (isTokenExpired(token)) {
    clearAuthData();
    getStore().dispatch(getLogoutUser()(null));
    redirectToLogin();
    return false;
  }
  
  return true;
};

// Helper function to create configured axios instances
export const createAxiosInstance = (config = {}) => {
  const instance = axios.create(config);
  
  // Apply the same request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      
      if (token) {
        if (isTokenExpired(token)) {
          redirectToLogin();
          return Promise.reject(new Error('Token expired'));
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // If data is FormData, remove Content-Type so the browser sets the correct
      // multipart/form-data boundary. Use AxiosHeaders.delete() for reliable
      // case-insensitive removal in Axios 1.x.
      if (config.data instanceof FormData) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
        } else {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Apply the same response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          clearAuthData();
          getStore().dispatch(getLogoutUser()(null));
          redirectToLogin();
        }
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
      
      if (error.response?.data?.message?.toLowerCase().includes('expired') ||
          error.response?.data?.message?.toLowerCase().includes('invalid token') ||
          error.response?.data?.message?.toLowerCase().includes('unauthorized')) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          clearAuthData();
          getStore().dispatch(getLogoutUser()(null));
          redirectToLogin();
        }
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
      
      return Promise.reject(error);
    }
  );
  
  return instance;
};

// Export axios instance
export default axios;

